import { useState, useRef } from 'react';

import Select from '@hiredigital/ui/Input/Select';

import Styles from './LocationSelect.module.scss';

// Optional message nicety: example cities for countries commonly confused with a
// same-named town elsewhere (e.g. "Philippine, Netherlands"). Detection is fully
// data-driven from the injected `countries` list; this map only enriches the hint
// text and we fall back to a generic suggestion when a country isn't listed here.
const EXAMPLE_CITIES_BY_CCA2 = {
  PH: 'Manila or Cebu',
  MX: 'Mexico City or Guadalajara',
  PE: 'Lima or Cusco',
  CA: 'Toronto or Vancouver',
  BR: 'São Paulo or Rio de Janeiro',
  CL: 'Santiago or Valparaíso',
  LB: 'Beirut or Tripoli',
  JO: 'Amman or Aqaba',
  GE: 'Tbilisi or Batumi',
  CU: 'Havana or Santiago de Cuba',
};

// A picked "city" whose name matches a country name but sits in a *different* country
// is almost always a mis-pick (e.g. "Philippine, Netherlands" when the user meant the
// country Philippines). Return a soft suggestion in that case, otherwise null.
export const getCountryConfusionHint = (location, countries) => {
  if (!countries?.length || !location) return null;

  const placeName = (
    location.structuredFormatting?.mainText ||
    location.terms?.[0]?.value ||
    ''
  ).trim();
  const placeCountry = (location.terms?.[location.terms.length - 1]?.value || '').trim();
  if (placeName.length < 4) return null;

  const normalizedPlace = placeName.toLowerCase();
  const normalizedPlaceCountry = placeCountry.toLowerCase();

  const matched = countries.find((c) => {
    const countryName = (c?.name || '').trim().toLowerCase();
    if (!countryName) return false;
    const looksLikeCountry =
      countryName === normalizedPlace || countryName.startsWith(normalizedPlace);
    return looksLikeCountry && countryName !== normalizedPlaceCountry;
  });
  if (!matched) return null;

  const examples = EXAMPLE_CITIES_BY_CCA2[matched.cca2?.toUpperCase()];
  const suggestion = examples ? ` such as ${examples}` : '';
  return `Did you mean the country ${matched.name}? Search by the name of a city there${suggestion}.`;
};

/**
 * Reusable Google Places city picker.
 *
 * Dependencies are injected so this component stays app-agnostic and can later move
 * to a shared package:
 *  - `loadOptions`     async paginate loader that returns city predictions.
 *  - `persistLocation` optional get-or-create call (e.g. postLocations). When present,
 *                      the picked place is saved before `onChange` fires, so a profile
 *                      save can safely reference its place_id. When absent (e.g. a
 *                      filter dropdown), the selection is emitted immediately.
 *  - `countries`       optional list ({ name, cca2 }) used only to surface the
 *                      country-confusion hint.
 *
 * `onChange` receives the selected prediction, or null when cleared / when a
 * persist attempt fails.
 */
const LocationSelect = ({
  label = 'Location',
  name = 'currentLocation',
  className = 'highlight-block',
  classNamePrefix = 's-contact',
  value,
  defaultValue,
  onChange,
  loadOptions,
  persistLocation,
  countries,
  error,
  ...props
}) => {
  const [saveError, setSaveError] = useState(null);
  const [hint, setHint] = useState(null);

  // Monotonic token so only the most recently selected location "wins" when several
  // get-or-create requests are in flight (out-of-order responses).
  const requestRef = useRef(0);

  const handleChange = (option) => {
    if (option.action === 'remove-value' || option.action === 'clear') {
      // Invalidate any in-flight selection so its late response can't re-populate the field.
      requestRef.current += 1;
      setSaveError(null);
      setHint(null);
      onChange?.(null);
      return;
    }

    if (option.action !== 'select-option') return;

    // Strip react-select's internal wrapper keys; keep just the Google prediction.
    const location = { ...option };
    delete location.target;
    delete location.action;

    const requestId = (requestRef.current += 1);
    setSaveError(null);
    setHint(getCountryConfusionHint(location, countries));

    if (!persistLocation) {
      onChange?.(location);
      return;
    }

    const country = location.terms ? location.terms[location.terms.length - 1].value : null;
    const data = {
      place_id: location.placeId,
      description: location.description,
      country: `${country}`,
      data: location,
    };
    // Persist (get-or-create) the location row before the parent references its place_id,
    // otherwise a freshly-picked place fails backend PK validation on save.
    persistLocation(data)
      .then(() => {
        if (requestRef.current !== requestId) return;
        onChange?.(location);
      })
      .catch(() => {
        if (requestRef.current !== requestId) return;
        // Drop the unsaved selection so the parent can't reference a place_id whose row
        // was never created, and tell the user to try again.
        setHint(null);
        setSaveError('We couldn’t save this location. Please select it again.');
        onChange?.(null);
      });
  };

  return (
    <>
      <Select
        className={className}
        classNamePrefix={classNamePrefix}
        name={name}
        label={label}
        value={value}
        defaultValue={defaultValue}
        error={error || saveError}
        isSavingMessage={() => 'Searching...'}
        noOptionsMessage={() => 'Start typing to search for a location'}
        getOptionLabel={({ description }) => description}
        getOptionValue={({ placeId }) => placeId}
        onChange={handleChange}
        loadOptions={loadOptions}
        isPaginated
        {...props}
      />
      {hint && <div className={Styles.hint}>{hint}</div>}
    </>
  );
};

export default LocationSelect;
